import { BsEye } from 'react-icons/bs';
import DocumentLinesModal from '../components/documents/DocumentLinesModal';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../utils/api';
import { formatCurrency, formatDate, downloadFile, previewFile, statusBadge } from '../utils/invoiceUtils';
import InvoiceKpiCards from '../components/invoices/InvoiceKpiCards';
import InvoiceSettingsModal from '../components/invoices/InvoiceSettingsModal';
import InvoiceWhatsappModal from '../components/invoices/InvoiceWhatsappModal';
import InvoiceEmailModal from '../components/invoices/InvoiceEmailModal';

const FacturasPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restoredList = location.state?.invoiceNavigation;
  const token = localStorage.getItem('token');
  const [invoices, setInvoices] = useState([]);
  const [invoiceTotals, setInvoiceTotals] = useState({ subtotal: 0, iva: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(restoredList?.page || 1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState(restoredList?.filters || { search: '', status: '', type: '', clientId: '', startDate: '', endDate: '' });
  // Texto del buscador (se vuelca a filters.search con debounce para no
  // disparar un fetch por cada tecla).
  const [searchInput, setSearchInput] = useState(restoredList?.filters?.search || '');
  // Controlador de la petición de facturas en curso (para cancelar la anterior
  // y evitar respuestas fuera de orden).
  const fetchAbortRef = useRef(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [settings, setSettings] = useState({
    cronEnabled: false,
    proformaSeries: '',
    proformaSeriesPad: 4,
    series: '',
    seriesPad: 4,
    autoSendProformas: false,
    autoSendTemplateId: '',
    autoSendAccountId: '',
    autoSendWhatsapp: false,
    autoSendWhatsappTemplate: 'envio_factura',
    defaultDueDays: 30,
    rectificativaSeries: '',
    rectificativaSeriesPad: 4
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [whatsappModal, setWhatsappModal] = useState({
    open: false,
    loading: false,
    sending: false,
    error: '',
    invoiceId: null,
    clientId: null,
    phone: '',
    clientName: '',
    invoiceNumber: '',
    templateName: 'envio_factura'
  });
  const [emailModal, setEmailModal] = useState({
    open: false,
    loading: false,
    sending: false,
    error: '',
    invoiceId: null,
    clientId: null,
    to: '',
    clientName: '',
    invoiceNumber: '',
    subject: '',
    body: ''
  });
  const [payModal, setPayModal] = useState({ open: false, invoice: null, saving: false });
  const [filterClientOptions, setFilterClientOptions] = useState([]);
  const [filterClientQuery, setFilterClientQuery] = useState('');
  const [filterClientsLoading, setFilterClientsLoading] = useState(false);
  // Acción en curso por fila: `${id}:${accion}` (p.ej. '12:facturar', '12:eliminar')
  const [actingId, setActingId] = useState(null);
  // Id de la fila cuyo menú "Acciones" está abierto (dropdown controlado por React)
  const [openMenuId, setOpenMenuId] = useState(null);
  const [linesDocument, setLinesDocument] = useState(null);

  const totalPages = useMemo(() => Math.max(Math.ceil(total / pageSize), 1), [total, pageSize]);

  const fetchInvoices = useCallback(async () => {
    // Cancela la petición anterior si sigue en vuelo: evita que una respuesta
    // antigua llegue después y pise la más reciente (race condition).
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      // El listado de facturas nunca incluye recurrentes (son plantillas, no facturas fiscales).
      params.append('recurrent', 'false');
      const res = await apiFetch(`/api/invoices/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo facturas');
      setHasLoaded(true);
      if (Array.isArray(data)) {
        setInvoices(data);
        setTotal(data.length);
        setInvoiceTotals({ subtotal: 0, iva: 0, total: 0 });
      } else {
        setInvoices(Array.isArray(data.items) ? data.items : []);
        setTotal(Number(data.total || 0));
        setInvoiceTotals(data.totals || { subtotal: 0, iva: 0, total: 0 });
      }
    } catch (err) {
      // Petición cancelada por una más reciente: no es un error para el usuario.
      if (err.name === 'AbortError') return;
      setError(err.message);
    } finally {
      // Solo apaga el "Cargando" si esta sigue siendo la petición vigente
      // (si fue abortada, la nueva ya ha puesto loading=true).
      if (fetchAbortRef.current === controller) setLoading(false);
    }
  }, [page, pageSize, filters, token, handleUnauthorized]);

  const loadKpis = useCallback(async () => {
    try {
      const res = await apiFetch('/api/invoices/kpis', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.ok) setKpis(data);
    } catch (_) { /* noop */ }
  }, [token]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await apiFetch('/api/invoices/settings', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) {
        setSettings({
          cronEnabled: Boolean(data?.cronEnabled),
          proformaSeries: data?.proformaSeries || '',
          proformaSeriesPad: data?.proformaSeriesPad || 4,
          series: data?.series || '',
          seriesPad: data?.seriesPad || 4,
          autoSendProformas: Boolean(data?.autoSendProformas),
          autoSendTemplateId: data?.autoSendTemplateId ? String(data.autoSendTemplateId) : '',
          autoSendAccountId: data?.autoSendAccountId ? String(data.autoSendAccountId) : '',
          autoSendWhatsapp: Boolean(data?.autoSendWhatsapp),
          autoSendWhatsappTemplate: data?.autoSendWhatsappTemplate || 'envio_factura',
          defaultDueDays: data?.defaultDueDays ?? 30,
          rectificativaSeries: data?.rectificativaSeries || '',
          rectificativaSeriesPad: data?.rectificativaSeriesPad || 4
        });
      }
    } catch (err) {
      console.error('Error cargando configuración de facturas', err);
    }
  }, [token, handleUnauthorized]);

  const syncAllInvoices = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const res = await apiFetch('/api/invoices/sync-all', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.status === 409) {
        setSyncMessage('Ya hay una sincronización en curso.');
        return;
      }
      if (!res.ok) throw new Error(data.message || 'Error sincronizando');
      setSyncMessage(`Facturas importadas: ${data.imported}, clientes creados: ${data.clientsCreated || 0}, total Dolibarr: ${data.total}`);
      fetchInvoices();
      loadKpis();
    } catch (err) {
      setSyncMessage(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const fetchClientsForFilter = useCallback(async (search = '') => {
    setFilterClientsLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, pageSize: 20 });
      if (search) params.append('search', search);
      const res = await apiFetch(`/api/clients?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando clientes');
      const options = (data?.data || []).map((c) => ({
        value: c.id,
        label: `${c.razon_social || `Cliente #${c.id}`}${c.email ? ` · ${c.email}` : ''}`,
        data: c
      }));
      setFilterClientOptions(options);
    } catch (err) {
      console.error(err.message);
    } finally {
      setFilterClientsLoading(false);
    }
  }, [token, handleUnauthorized]);

  const handleDownloadFile = async (endpoint, filename) => {
    try {
      await downloadFile(endpoint, filename, token, handleUnauthorized);
    } catch (err) {
      alert(err.message);
    }
  };

  const handlePreviewFile = async (endpoint) => {
    try {
      await previewFile(endpoint, token, handleUnauthorized);
    } catch (err) {
      alert(err.message);
    }
  };

  const openWhatsappModal = async (invoice) => {
    const baseNumber = invoice?.numero ? invoice.numero : `#${invoice?.id || ''}`;
    setWhatsappModal({
      open: true,
      loading: true,
      sending: false,
      error: '',
      invoiceId: invoice?.id || null,
      clientId: invoice?.client_id || null,
      phone: '',
      clientName: invoice?.razon_social || '',
      invoiceNumber: baseNumber,
      templateName: 'envio_factura'
    });
    if (!invoice?.client_id) {
      setWhatsappModal((prev) => ({ ...prev, loading: false, error: 'Factura sin cliente asociado.' }));
      return;
    }
    try {
      const res = await apiFetch(`/api/clients/${invoice.client_id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando cliente');
      setWhatsappModal((prev) => ({
        ...prev,
        loading: false,
        phone: data.telefono || prev.phone,
        clientName: data.razon_social || prev.clientName
      }));
    } catch (err) {
      setWhatsappModal((prev) => ({ ...prev, loading: false, error: err.message }));
    }
  };

  const sendWhatsappInvoice = async () => {
    if (!whatsappModal.invoiceId) return;
    if (!whatsappModal.phone.trim()) {
      setWhatsappModal((prev) => ({ ...prev, error: 'El teléfono es obligatorio.' }));
      return;
    }
    setWhatsappModal((prev) => ({ ...prev, sending: true, error: '' }));
    try {
      const res = await apiFetch(`/api/clients/invoices/${whatsappModal.invoiceId}/send-whatsapp-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ templateName: whatsappModal.templateName })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar por WhatsApp');
      setWhatsappModal((prev) => ({ ...prev, sending: false, open: false }));
    } catch (err) {
      setWhatsappModal((prev) => ({ ...prev, sending: false, error: err.message }));
    }
  };

  const openEmailModal = async (invoice) => {
    const baseNumber = invoice?.numero ? invoice.numero : `#${invoice?.id || ''}`;
    const nombre = invoice?.razon_social || '';
    setEmailModal({
      open: true,
      loading: true,
      sending: false,
      error: '',
      invoiceId: invoice?.id || null,
      clientId: invoice?.client_id || null,
      to: '',
      clientName: nombre,
      invoiceNumber: baseNumber,
      subject: `Factura ${baseNumber}`,
      body: `Hola${nombre ? ` ${nombre}` : ''},\n\nAdjuntamos tu factura ${baseNumber}.\n\nUn saludo,\nInterconecta`
    });
    if (!invoice?.client_id) {
      setEmailModal((prev) => ({ ...prev, loading: false, error: 'Factura sin cliente asociado.' }));
      return;
    }
    try {
      const res = await apiFetch(`/api/clients/${invoice.client_id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando cliente');
      setEmailModal((prev) => ({
        ...prev,
        loading: false,
        to: data.email || prev.to,
        clientName: data.razon_social || prev.clientName
      }));
    } catch (err) {
      setEmailModal((prev) => ({ ...prev, loading: false, error: err.message }));
    }
  };

  const sendEmailInvoice = async () => {
    if (!emailModal.invoiceId) return;
    if (!emailModal.to.trim()) {
      setEmailModal((prev) => ({ ...prev, error: 'El destinatario es obligatorio.' }));
      return;
    }
    setEmailModal((prev) => ({ ...prev, sending: true, error: '' }));
    try {
      const res = await apiFetch(`/api/clients/invoices/${emailModal.invoiceId}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: emailModal.to.trim(), subject: emailModal.subject, body: emailModal.body })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar por email');
      setEmailModal((prev) => ({ ...prev, sending: false, open: false }));
    } catch (err) {
      setEmailModal((prev) => ({ ...prev, sending: false, error: err.message }));
    }
  };

  const saveSettings = async () => {
    // /facturas solo edita series/numeración. Enviamos el objeto COMPLETO de
    // settings (incluidos cronEnabled/autoSend* tal como se cargaron) para no
    // pisar la configuración del cron que ahora se edita en /facturas-recurrentes.
    setSavingSettings(true);
    try {
      const res = await apiFetch('/api/invoices/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setSettings({
        cronEnabled: Boolean(data?.cronEnabled),
        proformaSeries: data?.proformaSeries || '',
        proformaSeriesPad: data?.proformaSeriesPad || settings.proformaSeriesPad,
        series: data?.series || '',
        seriesPad: data?.seriesPad || settings.seriesPad,
        autoSendProformas: Boolean(data?.autoSendProformas),
        autoSendTemplateId: data?.autoSendTemplateId ? String(data.autoSendTemplateId) : '',
        autoSendAccountId: data?.autoSendAccountId ? String(data.autoSendAccountId) : '',
        autoSendWhatsapp: Boolean(data?.autoSendWhatsapp),
        autoSendWhatsappTemplate: data?.autoSendWhatsappTemplate || 'envio_factura',
        defaultDueDays: data?.defaultDueDays ?? settings.defaultDueDays,
        rectificativaSeries: data?.rectificativaSeries || '',
        rectificativaSeriesPad: data?.rectificativaSeriesPad || settings.rectificativaSeriesPad
      });
      setConfigOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (actingId) return;
    setOpenMenuId(null);
    if (!window.confirm('¿Eliminar esta factura?')) return;
    setActingId(`${invoiceId}:eliminar`);
    try {
      const res = await apiFetch(`/api/clients/invoices/${invoiceId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error eliminando factura');
      fetchInvoices();
      loadKpis();
    } catch (err) {
      alert(err.message);
    } finally {
      setActingId(null);
    }
  };

  const markInvoicePaid = async () => {
    const inv = payModal.invoice;
    if (!inv) return;
    setPayModal((p) => ({ ...p, saving: true }));
    try {
      const res = await apiFetch(`/api/invoices/${inv.id}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && (data.error || data.message)) || 'No se pudo marcar como pagada');
      setPayModal({ open: false, invoice: null, saving: false });
      fetchInvoices();
      loadKpis();
    } catch (err) {
      alert(err.message);
      setPayModal((p) => ({ ...p, saving: false }));
    }
  };

  const duplicateInvoice = async (invoiceId) => {
    if (actingId) return;
    setOpenMenuId(null);
    setActingId(`${invoiceId}:duplicar`);
    try {
      const res = await apiFetch(`/api/clients/invoices/${invoiceId}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo duplicar la factura');
      // La copia queda en borrador editable: abrimos directamente su edición.
      navigate(`/facturas/${data.id}/editar`);
    } catch (err) {
      alert(err.message);
    } finally {
      setActingId(null);
    }
  };

  const facturarProforma = async (invoiceId) => {
    if (actingId) return;
    setOpenMenuId(null);
    if (!window.confirm('¿Crear factura normal desde esta proforma?')) return;
    setActingId(`${invoiceId}:facturar`);
    try {
      const res = await apiFetch(`/api/clients/invoices/${invoiceId}/facturar`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error facturando proforma');
      alert(`Factura creada: ${data.numero || ''}`);
      fetchInvoices();
      loadKpis();
    } catch (err) {
      alert(err.message);
    } finally {
      setActingId(null);
    }
  };

  const emitirFactura = async (invoiceId) => {
    if (actingId) return;
    setOpenMenuId(null);
    if (!window.confirm('¿Emitir esta factura? Se le asignará el número definitivo y pasará a Pendiente. NO se envía nada al cliente.')) return;
    setActingId(`${invoiceId}:emitir`);
    try {
      const res = await apiFetch(`/api/clients/invoices/${invoiceId}/emit`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error emitiendo la factura');
      alert(`Factura emitida: ${data.numero || ''}`);
      fetchInvoices();
      loadKpis();
    } catch (err) {
      alert(err.message);
    } finally {
      setActingId(null);
    }
  };

  const toggleVisibility = async (invoice) => {
    const id = invoice?.id;
    if (!id) return;
    const current = invoice.visible_cliente !== false;
    const next = !current;
    // Actualización optimista del estado local
    setInvoices((prev) => prev.map((it) => (it.id === id ? { ...it, visible_cliente: next } : it)));
    try {
      const res = await apiFetch(`/api/clients/invoices/${id}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ visible_cliente: next })
      });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        throw new Error(data.message || 'No se pudo actualizar la visibilidad');
      }
    } catch (err) {
      // Revertir si falla
      setInvoices((prev) => prev.map((it) => (it.id === id ? { ...it, visible_cliente: current } : it)));
      alert(err.message);
    }
  };

  const selectedFilterClient = useMemo(() => {
    if (!filters.clientId) return null;
    const parsed = Number(filters.clientId);
    return filterClientOptions.find((opt) => opt.value === parsed)
      || { value: parsed, label: `Cliente #${filters.clientId}` };
  }, [filters.clientId, filterClientOptions]);

  useEffect(() => {
    fetchInvoices();
    loadKpis();
  }, [fetchInvoices, loadKpis]);

  // Debounce del buscador: vuelca searchInput a filters.search pasados ~350ms
  // sin teclear (un solo fetch en vez de uno por tecla).
  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((p) => (p.search === searchInput ? p : { ...p, search: searchInput }));
      if (filters.search !== searchInput) setPage(1);
    }, 350);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search]);

  // Reajusta la página si queda fuera de rango (p.ej. al eliminar el último
  // elemento de una página > 1). totalPages tiene mínimo 1, así que esto lleva
  // a la última página válida y vuelve a disparar fetchInvoices vía su dependencia de page.
  useEffect(() => {
    if (hasLoaded && !loading && page > totalPages) setPage(totalPages);
  }, [totalPages, page, hasLoaded, loading]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const query = filterClientQuery.trim();
    const handler = setTimeout(() => {
      fetchClientsForFilter(query);
    }, 250);
    return () => clearTimeout(handler);
  }, [filterClientQuery, fetchClientsForFilter]);

  // Cierra el dropdown de acciones al hacer clic fuera de él
  useEffect(() => {
    if (openMenuId == null) return undefined;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.invoice-actions-menu')) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  return (
    <div className="small">
      {linesDocument && <DocumentLinesModal document={linesDocument} kind="invoices" onClose={() => setLinesDocument(null)} />}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Facturas</h2>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/facturas/nueva')}>
            Crear factura
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/facturas-recurrentes')}>
            Facturas recurrentes
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={syncAllInvoices}
            disabled={syncing}
          >
            {syncing ? 'Sincronizando...' : 'Sincronizar facturas Dolibarr'}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={async () => {
              await loadSettings();
              setConfigOpen(true);
            }}
          >
            Configuración
          </button>
          {loading && <span className="badge bg-info text-dark">Cargando...</span>}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {syncMessage && <div className="alert alert-info alert-dismissible fade show">{syncMessage}<button type="button" className="btn-close" onClick={() => setSyncMessage('')}></button></div>}

      <InvoiceKpiCards
        kpis={kpis}
        onClickVencido={() => { setPage(1); setFilters((p) => ({ ...p, status: 'Vencida' })); }}
      />

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Todas las facturas</h5>
            <div className="text-muted small">Total: {total}</div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-md-4">
              <input
                className="form-control form-control-sm"
                placeholder="Buscar por cliente, email o número"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={filters.status}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, status: e.target.value })); }}
              >
                <option value="">Todos los estados</option>
                <option value="Borrador">Borrador</option>
                <option value="Pendiente">Pendiente</option>
                <option value="Validada">Validada</option>
                <option value="Parcial">Parcial</option>
                <option value="Pagada">Pagada</option>
                {/* "Vencida" no es un estado almacenado: el backend lo resuelve por fecha de
                    vencimiento + estado pendiente (igual que el badge calculado en la columna). */}
                <option value="Vencida">Vencida</option>
                <option value="Anulada">Anulada</option>
                <option value="Devuelta">Devuelta</option>
                <option value="Emitida">Emitida</option>
                <option value="Facturada">Facturada</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={filters.type}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, type: e.target.value })); }}
              >
                <option value="">Todos los tipos</option>
                <option value="normal">Normal</option>
                <option value="proforma">Proforma</option>
              </select>
            </div>
            <div className="col-md-2">
              <Select
                classNamePrefix="select"
                isLoading={filterClientsLoading}
                options={filterClientOptions}
                value={selectedFilterClient}
                placeholder="Cliente"
                onChange={(opt) => { setPage(1); setFilters((p) => ({ ...p, clientId: opt ? String(opt.value) : '' })); }}
                onInputChange={(val, action) => {
                  if (action.action === 'input-change') setFilterClientQuery(val);
                  return val;
                }}
                isClearable
                noOptionsMessage={() => 'Sin resultados'}
              />
            </div>
          </div>
          <div className="row g-2 mb-3">
            <div className="col-md-3">
              <input
                className="form-control form-control-sm"
                type="date"
                value={filters.startDate}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, startDate: e.target.value })); }}
              />
            </div>
            <div className="col-md-3">
              <input
                className="form-control form-control-sm"
                type="date"
                value={filters.endDate}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, endDate: e.target.value })); }}
              />
            </div>
            <div className="col-md-3">
              <button
                className="btn btn-outline-secondary btn-sm w-100"
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setFilters({ search: '', status: '', type: '', clientId: '', startDate: '', endDate: '' });
                  setPage(1);
                }}
              >
                Limpiar
              </button>
            </div>
          </div>
          {loading ? (
            <p>Cargando...</p>
          ) : invoices.length ? (
            <div className="table-responsive">
              <table className="table table-striped table-sm align-middle">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Número</th>
                    <th>Fecha</th>
                    <th>Vencimiento</th>
                    <th>Estado</th>
                    <th>Subtotal</th>
                    <th>IVA</th>
                    <th>Total</th>
                    <th className="text-center" title="Visible en el área cliente">Área cliente</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td>
                        {inv.client_id ? (
                          <Link to={`/clientes/${inv.client_id}`} className="text-decoration-none">
                            {inv.razon_social || `Cliente #${inv.client_id}`}
                          </Link>
                        ) : (inv.razon_social || '-')}
                      </td>
                      <td>{inv.numero || (String(inv.estado || '').toLowerCase() === 'borrador'
                        ? <span className="badge bg-light text-secondary border">Provisional</span>
                        : '-')}</td>
                      <td>{formatDate(inv.fecha)}</td>
                      <td>
                        {inv.fecha_vencimiento ? formatDate(inv.fecha_vencimiento) : '-'}
                        {inv.fecha_vencimiento
                          && ['Pendiente', 'Parcial', 'Validada'].includes(inv.estado)
                          && new Date(inv.fecha_vencimiento) < new Date(new Date().toDateString()) && (
                          <span className="badge bg-danger ms-2">Vencida</span>
                        )}
                      </td>
                      <td>
                        {inv.estado
                          ? <span className={`badge ${statusBadge(inv.estado)}`}>{inv.estado}</span>
                          : '-'}
                        {inv.invoice_type === 'proforma' && (
                          <span className="badge bg-primary ms-2">Proforma</span>
                        )}
                        {inv.invoice_type === 'rectificativa' && (
                          <span className="badge bg-warning text-dark ms-2">Rectificativa</span>
                        )}
                      </td>
                      <td>{formatCurrency(inv.subtotal)}</td>
                      <td>{formatCurrency(inv.iva)}</td>
                      <td>{formatCurrency(inv.total)}</td>
                      <td className="text-center">
                        <div
                          className="form-check form-switch d-inline-flex justify-content-center m-0"
                          title={inv.visible_cliente !== false ? 'Visible en el área cliente' : 'Oculta en el área cliente'}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={inv.visible_cliente !== false}
                            onChange={() => toggleVisibility(inv)}
                            aria-label="Visible en el área cliente"
                          />
                        </div>
                      </td>
                      <td className="text-end">
                        {(() => {
                          const isProforma = inv.invoice_type === 'proforma';
                          const canFacturar = isProforma && !inv.dolibarr_invoice_id && String(inv.estado || '') !== 'Facturada';
                          const canEliminar = !inv.dolibarr_invoice_id
                            && ['borrador', 'proforma'].includes(String(inv.estado || '').toLowerCase());
                          const canEmitir = !inv.dolibarr_invoice_id && !inv.is_recurrent
                            && !['proforma', 'rectificativa'].includes(String(inv.invoice_type || 'normal').toLowerCase())
                            && String(inv.estado || '').toLowerCase() === 'borrador';
                          const facturando = actingId === `${inv.id}:facturar`;
                          const eliminando = actingId === `${inv.id}:eliminar`;
                          const emitiendo = actingId === `${inv.id}:emitir`;
                          const rowBusy = facturando || eliminando || emitiendo;
                          return (
                            <div className="btn-group btn-group-sm invoice-actions-menu" role="group" style={{ position: 'relative' }}>
                              <button type="button" className="btn btn-outline-secondary" title="Ver líneas" aria-label={`Ver líneas de factura ${inv.numero || inv.id}`} onClick={() => { setOpenMenuId(null); setLinesDocument(inv); }}><BsEye aria-hidden="true" /></button>
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => navigate(`/facturas/${inv.id}`, { state: { invoiceNavigation: { ids: invoices.map((item) => item.id), page, pageSize, total, filters, returnTo: '/facturas', query: { pageSize, recurrent: 'false', ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)) } } } })}
                              >
                                Ver
                              </button>
                              {canFacturar && (
                                <button
                                  className="btn btn-outline-success"
                                  type="button"
                                  disabled={rowBusy}
                                  onClick={() => facturarProforma(inv.id)}
                                >
                                  {facturando ? (
                                    <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Facturando…</>
                                  ) : 'Facturar'}
                                </button>
                              )}
                              {canEmitir && (
                                <button
                                  className="btn btn-outline-success"
                                  type="button"
                                  disabled={rowBusy}
                                  onClick={() => emitirFactura(inv.id)}
                                  title="Asigna el número definitivo y pasa a Pendiente (no envía nada al cliente)"
                                >
                                  {emitiendo ? (
                                    <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Emitiendo…</>
                                  ) : 'Emitir'}
                                </button>
                              )}
                              <button
                                className="btn btn-outline-secondary dropdown-toggle"
                                type="button"
                                aria-expanded={openMenuId === inv.id}
                                onClick={() => setOpenMenuId((prev) => (prev === inv.id ? null : inv.id))}
                              >
                                Acciones
                              </button>
                              <ul
                                className={`dropdown-menu dropdown-menu-end${openMenuId === inv.id ? ' show' : ''}`}
                                style={{ position: 'absolute', right: 0, top: '100%' }}
                              >
                                <li>
                                  <button
                                    className="dropdown-item"
                                    type="button"
                                    disabled={actingId === `${inv.id}:duplicar`}
                                    onClick={() => { setOpenMenuId(null); duplicateInvoice(inv.id); }}
                                  >
                                    {actingId === `${inv.id}:duplicar` ? 'Duplicando…' : 'Duplicar'}
                                  </button>
                                </li>
                                <li><hr className="dropdown-divider" /></li>
                                <li>
                                  <button
                                    className="dropdown-item"
                                    type="button"
                                    onClick={() => { setOpenMenuId(null); handlePreviewFile(`/api/clients/invoices/${inv.id}/${inv.dolibarr_invoice_id ? 'dolibarr-pdf' : 'pdf'}`); }}
                                  >
                                    Previsualizar PDF
                                  </button>
                                </li>
                                {inv.dolibarr_invoice_id && (
                                  <li>
                                    <button
                                      className="dropdown-item"
                                      type="button"
                                      onClick={() => { setOpenMenuId(null); handleDownloadFile(`/api/clients/invoices/${inv.id}/dolibarr-pdf`, `${inv.numero || 'factura'}.pdf`); }}
                                    >
                                      PDF Dolibarr
                                    </button>
                                  </li>
                                )}
                                <li>
                                  <button
                                    className="dropdown-item"
                                    type="button"
                                    onClick={() => { setOpenMenuId(null); handleDownloadFile(`/api/clients/invoices/${inv.id}/pdf`, `${inv.numero || 'factura'}.pdf`); }}
                                  >
                                    PDF
                                  </button>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item"
                                    type="button"
                                    onClick={() => { setOpenMenuId(null); openEmailModal(inv); }}
                                  >
                                    Email
                                  </button>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item"
                                    type="button"
                                    onClick={() => { setOpenMenuId(null); openWhatsappModal(inv); }}
                                  >
                                    WhatsApp
                                  </button>
                                </li>
                                {['Pendiente', 'Parcial', 'Validada'].includes(inv.estado)
                                  && !inv.is_recurrent
                                  && inv.invoice_type !== 'proforma' && inv.invoice_type !== 'rectificativa' && (
                                  <li>
                                    <button
                                      className="dropdown-item text-success fw-semibold"
                                      type="button"
                                      onClick={() => { setOpenMenuId(null); setPayModal({ open: true, invoice: inv, saving: false }); }}
                                    >
                                      Marcar pagada
                                    </button>
                                  </li>
                                )}
                                {canEliminar && (
                                  <>
                                    <li><hr className="dropdown-divider" /></li>
                                    <li>
                                      <button
                                        className="dropdown-item text-danger"
                                        type="button"
                                        disabled={rowBusy}
                                        onClick={() => deleteInvoice(inv.id)}
                                      >
                                        {eliminando ? (
                                          <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Eliminando…</>
                                        ) : 'Eliminar'}
                                      </button>
                                    </li>
                                  </>
                                )}
                              </ul>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-light fw-bold">
                    <td colSpan={5} className="text-end">Totales (filtro actual):</td>
                    <td>{formatCurrency(invoiceTotals.subtotal)}</td>
                    <td>{formatCurrency(invoiceTotals.iva)}</td>
                    <td>{formatCurrency(invoiceTotals.total)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p>No hay facturas.</p>
          )}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">Página {page} de {totalPages} (total {total})</div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>

      <InvoiceSettingsModal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        settings={settings}
        setSettings={setSettings}
        savingSettings={savingSettings}
        onSave={saveSettings}
      />

      <InvoiceWhatsappModal
        state={whatsappModal}
        setState={setWhatsappModal}
        onClose={() => setWhatsappModal((prev) => ({ ...prev, open: false, error: '' }))}
        onSend={sendWhatsappInvoice}
      />

      <InvoiceEmailModal
        state={emailModal}
        setState={setEmailModal}
        onClose={() => setEmailModal((prev) => ({ ...prev, open: false, error: '' }))}
        onSend={sendEmailInvoice}
      />

      {payModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Marcar factura como pagada</h5>
                <button className="btn-close" type="button" disabled={payModal.saving} onClick={() => setPayModal({ open: false, invoice: null, saving: false })}></button>
              </div>
              <div className="modal-body">
                <p className="mb-1">
                  ¿Confirmas que la factura <strong>{payModal.invoice?.numero || payModal.invoice?.serie || `#${payModal.invoice?.id}`}</strong> está pagada?
                </p>
                <p className="text-muted small mb-0">
                  Se registrará el pago del importe pendiente y la factura quedará como <strong>Pagada</strong>.
                  {payModal.invoice?.dolibarr_invoice_id ? ' El pago también se registrará en Dolibarr.' : ''}
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" type="button" disabled={payModal.saving} onClick={() => setPayModal({ open: false, invoice: null, saving: false })}>Cancelar</button>
                <button className="btn btn-success" type="button" disabled={payModal.saving} onClick={markInvoicePaid}>
                  {payModal.saving ? 'Guardando...' : 'Confirmar pagada'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FacturasPage;
